import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { ImageLightbox } from '../components/ImageLightbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FotoUploader } from '../components/FotoUploader';
import { Plus, Heart, Trash2, ExternalLink, Tag, ShoppingBag, Package } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE = [
  { value: 'pezzo_moto', labelKey: 'wishlist.categories.motoPart' },
  { value: 'abbigliamento', labelKey: 'wishlist.categories.clothing' },
  { value: 'accessorio', labelKey: 'wishlist.categories.accessory' },
  { value: 'altro', labelKey: 'wishlist.categories.other' },
];

export default function Wishlist() {
  const { t } = useTranslation();
  const { user, selectedMotoId } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  const [formData, setFormData] = useState({
    nome: '',
    categoria: 'pezzo_moto',
    descrizione: '',
    prezzo: '',
    valuta: user?.valuta || 'CHF',
    link_acquisto: '',
    priorita: 'media',
    foto_urls: []
  });

  useEffect(() => {
    loadItems();
  }, [selectedMotoId]);

  const loadItems = async () => {
    try {
      const response = await api.get('/wishlist', {
        params: selectedMotoId ? { moto_id: selectedMotoId } : {}
      });
      setItems(response.data);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      categoria: 'pezzo_moto',
      descrizione: '',
      prezzo: '',
      valuta: user?.valuta || 'CHF',
      link_acquisto: '',
      priorita: 'media',
      foto_urls: []
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        nome: item.nome,
        categoria: item.categoria,
        descrizione: item.descrizione || '',
        prezzo: item.prezzo?.toString() || '',
        valuta: item.valuta || user?.valuta || 'CHF',
        link_acquisto: item.link_acquisto || '',
        priorita: item.priorita || 'media',
        foto_urls: item.foto_urls || []
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome) {
      toast.error(t('wishlist.nameRequired'));
      return;
    }

    try {
      const payload = {
        ...formData,
        prezzo: formData.prezzo ? parseFloat(formData.prezzo) : null,
        moto_id: selectedMotoId
      };

      if (editingItem) {
        await api.put(`/wishlist/${editingItem.id}`, payload);
        toast.success(t('success.updated'));
      } else {
        await api.post('/wishlist', payload);
        toast.success(t('success.saved'));
      }
      
      setDialogOpen(false);
      resetForm();
      loadItems();
    } catch (error) {
      toast.error(t('errors.saveError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('wishlist.deleteConfirm'))) return;
    
    try {
      await api.delete(`/wishlist/${id}`);
      toast.success(t('success.deleted'));
      loadItems();
    } catch (error) {
      toast.error(t('errors.deleteError'));
    }
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const formatPrice = (prezzo, valuta) => {
    if (!prezzo) return null;
    const symbols = { CHF: 'CHF', EUR: '€', USD: '$', GBP: '£' };
    return `${symbols[valuta] || valuta} ${prezzo.toLocaleString('de-CH', { minimumFractionDigits: 2 })}`;
  };

  const getCategoryIcon = (categoria) => {
    switch(categoria) {
      case 'pezzo_moto': return <Package className="w-4 h-4" />;
      case 'abbigliamento': return <ShoppingBag className="w-4 h-4" />;
      default: return <Tag className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priorita) => {
    switch(priorita) {
      case 'alta': return 'text-red-500';
      case 'media': return 'text-yellow-500';
      case 'bassa': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Heart className="w-8 h-8 text-[#FF5F1F]" />
              {t('nav.wishlist')}
            </h1>
            <p className="page-subtitle">{t('wishlist.subtitle')}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold"
                data-testid="add-wishlist-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('wishlist.addItem')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                  {editingItem ? t('wishlist.editItem') : t('wishlist.addItem')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t('wishlist.itemName')}</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                    data-testid="wishlist-nome"
                  />
                </div>

                <div>
                  <Label className="form-label">{t('wishlist.category')}</Label>
                  <Select value={formData.categoria} onValueChange={(v) => setFormData({...formData, categoria: v})}>
                    <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                      {CATEGORIE.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {t(cat.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t('wishlist.price')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.prezzo}
                      onChange={(e) => setFormData({...formData, prezzo: e.target.value})}
                      onFocus={(e) => e.target.select()}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      data-testid="wishlist-prezzo"
                    />
                  </div>
                  <div>
                    <Label className="form-label">{t('auth.currency')}</Label>
                    <Select value={formData.valuta} onValueChange={(v) => setFormData({...formData, valuta: v})}>
                      <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                        <SelectItem value="CHF">CHF</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="form-label">{t('wishlist.priority')}</Label>
                  <Select value={formData.priorita} onValueChange={(v) => setFormData({...formData, priorita: v})}>
                    <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                      <SelectItem value="alta">{t('wishlist.priorities.high')}</SelectItem>
                      <SelectItem value="media">{t('wishlist.priorities.medium')}</SelectItem>
                      <SelectItem value="bassa">{t('wishlist.priorities.low')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="form-label">{t('wishlist.purchaseLink')}</Label>
                  <Input
                    type="url"
                    value={formData.link_acquisto}
                    onChange={(e) => setFormData({...formData, link_acquisto: e.target.value})}
                    placeholder="https://"
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                    data-testid="wishlist-link"
                  />
                </div>

                <div>
                  <Label className="form-label">{t('wishlist.description')}</Label>
                  <Textarea
                    value={formData.descrizione}
                    onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                    className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none min-h-[80px]"
                  />
                </div>

                <div>
                  <Label className="form-label">{t('moto.photos')}</Label>
                  <FotoUploader
                    fotoUrls={formData.foto_urls}
                    onChange={(urls) => setFormData({...formData, foto_urls: urls})}
                    maxFiles={3}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1 border-[#262626] hover:bg-[#262626]"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                  >
                    {t('common.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#9CA3AF]">{t('common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="vault-card text-center py-12">
            <Heart className="w-16 h-16 text-[#FF5F1F]/30 mx-auto mb-4" />
            <p className="text-[#9CA3AF] text-lg mb-2">{t('wishlist.noItems')}</p>
            <p className="text-[#6B7280] text-sm">{t('wishlist.startAdding')}</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <div key={item.id} className="vault-card p-4" data-testid={`wishlist-item-${item.id}`}>
                {/* Image */}
                {item.foto_urls && item.foto_urls.length > 0 && (
                  <div 
                    className="w-full h-40 mb-4 bg-[#121212] border border-[#262626] cursor-pointer overflow-hidden"
                    onClick={() => openLightbox(item.foto_urls, 0)}
                  >
                    <img 
                      src={getImageUrl(item.foto_urls[0])} 
                      alt={item.nome}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </div>
                )}
                
                {/* Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[#FF5F1F]">{getCategoryIcon(item.categoria)}</span>
                    <h3 className="text-white font-semibold">{item.nome}</h3>
                  </div>
                  <span className={`text-xs uppercase ${getPriorityColor(item.priorita)}`}>
                    {item.priorita}
                  </span>
                </div>

                {/* Price */}
                {item.prezzo && (
                  <p className="text-[#FF5F1F] font-mono text-lg mb-2">
                    {formatPrice(item.prezzo, item.valuta)}
                  </p>
                )}

                {/* Description */}
                {item.descrizione && (
                  <p className="text-[#9CA3AF] text-sm mb-3 line-clamp-2">{item.descrizione}</p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-[#262626]">
                  {item.link_acquisto && (
                    <a
                      href={item.link_acquisto}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[#9CA3AF] hover:text-[#FF5F1F] text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('wishlist.buyNow')}
                    </a>
                  )}
                  <div className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(item)}
                    className="text-[#9CA3AF] hover:text-white"
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    className="text-red-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Layout>
  );
}
